import {useState, useRef, useEffect} from "react";
import{matchPath} from "react-router-dom"
import useAuth from "ubcommunity-shared/src/Auth/hooks/useAuth";
import {
    GATEWAY_URL,
    ACVL_APPLICATION_ID
} from "ubcommunity-shared/src/constants";
import removeTrailingSlash from "ubcommunity-shared/src/Utils/remove-trailing-slash"

const ENDPOINT_URL = `${removeTrailingSlash(GATEWAY_URL)}/acvl/auth-code`
const CLIENT_KEY = `${ACVL_APPLICATION_ID}-`

type AccessTokenType = {
    scheme: string,
    token: string
}

export default function useSSOAuthCode (): {accessToken: AccessTokenType | null, error: string | null} {
    const {authState, setAuth} = useAuth()
    const [accessToken, setAccessToken] = useState(null)
    const [error, setError] = useState(null)
    const acvlRequest = useRef(null);

    const {location:{search, origin}} = window
    const urlSearchParams = new URLSearchParams(search);
    const state = urlSearchParams.get('state');
    const code = urlSearchParams.get('code');
    const {params: {universitySlug}} = matchPath<{
        universitySlug: string;
    }>(state, { path: '/u/:universitySlug' })
    const redirectUrl = `${origin}/u/${universitySlug}/auth/sso`

    useEffect(() => {
        if (acvlRequest.current) return

        const urlSearchParams = new URLSearchParams({
            code,
            client_key: CLIENT_KEY,
            redirect_url: redirectUrl, // `origin` is required (see the const declaration)
        });

        acvlRequest.current = (
            fetch(ENDPOINT_URL, {
                method: "POST",
                credentials: "include",
                headers: {
                    "content-type": "application/x-www-form-urlencoded",
                },
                body: urlSearchParams.toString(),
            })
                .then((response: Response): Promise<AccessTokenType> => {
                    if (response.ok) return response.json()

                    throw new Error(`Gateway status not expected. Recieved "${response.status}"`)
                })
                .then((accessToken: AccessTokenType) => {
                    setAccessToken(accessToken)
                    setError(null)
                })
                .catch(({ message }) => {
                    setAccessToken(null)
                    setError(message)
                })
        )
    }, [code, redirectUrl])

    useEffect(() => {
        if (accessToken) {
            if (authState?.accessToken !== accessToken) {
                setAuth({ ...authState, accessToken })
            }
        }
    }, [accessToken, authState, setAuth])

    return {accessToken, error}
}
